/**
 * useProjectCreation — 맵 프로젝트 생성 + 템플릿 적용 공용 훅.
 *
 * 대시보드의 생성 모달과 가이드 셋업 페이지(/map-editor/guided)가 같은
 * 경로를 쓴다: (name, floorName) 중복 검사 → localStorage lot 생성 →
 * API 생성 → id 동기화, 이어서 템플릿 로드/적용 + 객체 타입 시드.
 * 실패는 throw — 호출부가 각자 방식(모달 에러/페이지 에러)으로 표시한다.
 */
import { useProjectStore } from '../../map-editor-shared/store'
import { useObjectTypeStore } from '../../map-editor-shared/store/objectTypeStore'
import { useProjects, useCreateProject } from '../../map-editor-shared/hooks'
import { loadTemplate, type TemplateId } from '../../map-editor-features/template'
import { objectTypeApi, mapDataService } from '../../map-editor-shared/api'

export interface ProjectCreationInput {
  name: string
  description?: string
  floorName?: string
  cctvIpPrefix?: string
}

export function useProjectCreation(siteProjectId: string) {
  const { createLot, updateLot, setCurrentLot, applyTemplate } = useProjectStore()
  const {
    setCurrentLot: setObjectTypeLot,
    setCurrentProjectId,
    addTypes,
    clearTypes,
    loadTypes,
  } = useObjectTypeStore()
  const { data: apiProjects } = useProjects(Number(siteProjectId) || 0)
  const createProjectMutation = useCreateProject()

  /** 중복 검사 + 생성. 성공 시 최종 lot id 반환. */
  const createProject = async (data: ProjectCreationInput): Promise<string> => {
    // (name, floorName) duplicate detection — runs against the SERVER list
    // shown in the dashboard, not the localStorage cache. We block creation
    // only when both the project name AND the requested first-floor name
    // already exist together on a real server project.
    const requestedFloorName = (data.floorName?.trim() || 'B1')
    const sameNameProjects = (apiProjects ?? []).filter(
      p => p.name.toLowerCase() === data.name.toLowerCase()
    )
    for (const sameNameProject of sameNameProjects) {
      try {
        const resp = await mapDataService.getMapDataParsed(sameNameProject.id)
        const floors: Array<{ name?: string }> = []
        for (const item of resp.data ?? []) {
          const md = item.map_data as { floors?: Array<{ name?: string }> } | null
          if (md?.floors) floors.push(...md.floors)
        }
        const conflictFloor = floors.find(
          f => (f.name || '').toLowerCase() === requestedFloorName.toLowerCase()
        )
        if (conflictFloor) {
          throw new Error(
            `이미 같은 프로젝트 이름("${data.name}")과 층 이름("${requestedFloorName}") 조합이 존재합니다.`
          )
        }
      } catch (fetchErr) {
        if (fetchErr instanceof Error && fetchErr.message.startsWith('이미 같은')) {
          throw fetchErr
        }
        // If we can't read the existing project's floors, fall through —
        // do not block creation on a network failure of the duplicate check.
        console.warn('[useProjectCreation] failed to verify floor conflict for project', sameNameProject.id, fetchErr)
      }
    }

    // Create new lot in localStorage first (store-level check is gone).
    const newLot = createLot(data)

    // Create via API
    const apiProject = await createProjectMutation.mutateAsync({
      name: data.name,
      site_project_id: Number(siteProjectId) || 0,
      // description은 백엔드 API에 없으므로 제외
      // CCTV IP 앞자리: 생성 시에도 저장해야 이후 DXF 변환에서 CCTV IP 추출이 동작한다.
      cctv_ip_prefix: data.cctvIpPrefix ?? '',
    })

    // API에서 받은 ID로 localStorage 업데이트
    if (apiProject) {
      updateLot(newLot.id, { id: apiProject.id.toString() })
      return apiProject.id.toString()
    }
    return newLot.id
  }

  /** 템플릿 로드/적용 + 객체 타입 시드(로컬 + API). */
  const applyTemplateToProject = async (lotId: string, templateId: string) => {
    // Load template
    const template = await loadTemplate(templateId as TemplateId)

    // Apply template to project
    applyTemplate(lotId, template)

    // Set current lot in ObjectType store
    setObjectTypeLot(lotId)

    // Set project ID for API calls
    const projectId = parseInt(lotId, 10)
    setCurrentProjectId(projectId)

    // Clear existing object types
    clearTypes()

    // Convert template object types to ObjectType format and add them
    if (template.objectTypes) {
      const objectTypesToAdd = Object.entries(template.objectTypes).map(([, typeData]) => {
        const properties = Object.entries(typeData.defaultProperties || {}).map(([propKey, propValue]) => {
          let propType: 'string' | 'number' | 'boolean' | 'array'
          if (Array.isArray(propValue)) {
            propType = 'array'
          } else {
            const valueType = typeof propValue
            propType = (valueType === 'string' || valueType === 'number' || valueType === 'boolean')
              ? valueType
              : 'string'
          }

          return {
            key: propKey,
            type: propType,
            required: false,
            defaultValue: propValue,
          }
        })

        // Set default priority to 1 for "배경" type, otherwise use template priority or 5
        const isBackground = typeData.displayName === '배경' || typeData.displayName === 'background';
        const priority = (typeData as any).priority !== undefined
          ? (typeData as any).priority
          : (isBackground ? 1 : 5);

        return {
          name: typeData.displayName,
          icon: typeData.icon,
          color: typeData.defaultStyle?.color || '#000000',
          priority,
          properties,
        }
      })

      // Add types to local store
      addTypes(objectTypesToAdd)

      // Save template object types to API
      try {
        // Use batch create API to save all object types at once
        const createPromises = objectTypesToAdd.map(type =>
          objectTypeApi.create(projectId, {
            name: type.name,
            icon: type.icon,
            color: type.color,
            priority: type.priority,
            properties: type.properties
          })
        )

        await Promise.all(createPromises)

        // Reload types from API to sync with backend
        await loadTypes()
      } catch (apiError) {
        // Continue despite API error - types are still in localStorage
      }
    }

    setCurrentLot(lotId)
  }

  return { createProject, applyTemplateToProject }
}
